import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AlertTriangle, HelpCircle } from 'lucide-react'
import { Button, buttonStyles } from './Button'
import { cn } from './cn'

export interface ConfirmOptions {
  title: ReactNode
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Styles the confirm button as destructive (red). Use for delete/remove flows. */
  destructive?: boolean
}

type Resolver = (confirmed: boolean) => void

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

/**
 * Wrap your app in <ConfirmProvider>, then call `const confirm = useConfirm()`
 * and `await confirm({ title, body, destructive: true })` from event handlers.
 * Returns true if the user confirmed, false if they cancelled / dismissed.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: Resolver } | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>(resolve => setState({ opts, resolve }))
  }, [])

  const handle = useCallback((confirmed: boolean) => {
    setState(curr => {
      curr?.resolve(confirmed)
      return null
    })
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <Dialog opts={state.opts} onResult={handle} />}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within a <ConfirmProvider>')
  return ctx
}

function Dialog({ opts, onResult }: { opts: ConfirmOptions; onResult: (v: boolean) => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Focus cancel button by default — safer for destructive flows.
  useEffect(() => {
    cancelRef.current?.focus()
  }, [])

  // Esc → cancel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onResult(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onResult])

  const Icon = opts.destructive ? AlertTriangle : HelpCircle

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => onResult(false)}
      />

      {/* Card */}
      <div
        className={cn(
          'relative w-full max-w-md rounded-2xl bg-surface border border-line shadow-lg',
          'p-6 animate-scale-in origin-center',
        )}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div
            className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0',
              opts.destructive ? 'bg-error/10 text-error' : 'bg-brand/10 text-brand',
            )}
          >
            <Icon size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="confirm-title" className="font-display text-xl text-ink leading-tight">
              {opts.title}
            </h3>
            {opts.body && (
              <p className="mt-1.5 text-sm text-ink-muted">{opts.body}</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onResult(false)}
            className={buttonStyles({ variant: 'secondary', size: 'md' })}
          >
            {opts.cancelLabel ?? 'Cancel'}
          </button>
          <Button
            variant={opts.destructive ? 'destructive' : 'primary'}
            size="md"
            onClick={() => onResult(true)}
          >
            {opts.confirmLabel ?? (opts.destructive ? 'Delete' : 'Confirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}
